import { AccountService } from "@gtmhub/accounts/accountsService";
import { AssigneeType } from "./../../../assignees/models";
import { OkrMethodologyFacade } from "./../../../goals/components/facades/okr-methodology.facade";
import { IAccess, ISessionSettings } from "./../../models/models";
import { IIndicator, IUIError, createUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IComponentOptions, IRootScopeService, IQService, IPromise, IScope } from "angular";
import { ITeam, TeamsActions, ITeamsStoreState } from "@gtmhub/teams";
import { ICollection } from "@gtmhub/models";
import { GtmhubController } from "@gtmhub/core";
import { AnalyticsService, Flavor, recommendation } from "@gtmhub/core/analytics";
import { ICheckAccessFactory } from "@gtmhub/shared/factories/check-permissions";
import { INgRedux } from "@gtmhub/state-management";
import { StateParams, StateService } from "@uirouter/angularjs";
import { IModalService } from "angular-ui-bootstrap";
import { PlanningSessionService, PlanningSessionsActions, ISessionsStoreState } from "../..";
import { IdMap } from "@gtmhub/util";
import { CadenceType, IApiPermission, IPlanningSession, IPlanningSessionConflict, PlanningSessionStatus } from "../../models/models";
import colors from "wwwroot/json/colors.json";
import dayjs from "@gtmhub/dayjs";
import { PermissionsService } from "@gtmhub/users";
import { IAccessChecked } from "@gtmhub/shared/models";
import { FeatureToggleV2Service } from "@gtmhub/featureToggle";
import { IDynamicSubteamsRole, RoleService } from "@gtmhub/roles";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { localize } from "@gtmhub/localization";
import { IPrincipalKind } from "@gtmhub/sessions";
import { ApprovalWorkflowSelectionModal } from "../approval-workflow-selection-form/approval-workflow-selection-form.component";

interface ISessionFormComponentRedux {
  sessions: IdMap<IPlanningSession>;
  sessionsFetched: boolean;
  sessionsError: IUIError;
  teams: IdMap<ITeam>;
  teamsFetched: boolean;
  teamsError: IUIError;
}

const bindStateToCtrl = (state: ISessionsStoreState & ITeamsStoreState): ISessionFormComponentRedux => {
  const { map: sessions, isFetched: sessionsFetched, error: sessionsError } = state.sessions;
  const { map: teams, isFetched: teamsFetched, error: teamsError } = state.teams;

  return {
    sessions,
    sessionsFetched,
    sessionsError,
    teams,
    teamsFetched,
    teamsError,
  };
};

export interface ISessionFormScope extends IScope, IExtendedRootScopeService {}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SessionFormCtrl extends ISessionFormComponentRedux {}
export class SessionFormCtrl extends GtmhubController {
  planningSession: IPlanningSession;
  sessionSettings: ISessionSettings;
  permissions: { inheritsPermissions: boolean };
  indicators: {
    saving?: IIndicator;
    loading?: IIndicator;
  };
  conflicts: ICollection<IPlanningSessionConflict>;
  model = {
    dateRange: {
      startDate: dayjs().toISOString(),
      endDate: dayjs().add(1, "month").toISOString(),
    },
  };

  colors = colors.general;
  isApprovalWorkflowOpened = false;
  isSelectedFieldsChecked = false;
  sessionWorkflowSet: boolean;
  private defaultAccess: IAccess;
  private methodologyPerSessionEnabled: boolean;
  private operationType: "create" | "edit";
  private originalPlanningSession: IPlanningSession;

  private initialSessionDates = {
    start: this.model.dateRange.startDate,
    end: this.model.dateRange.endDate,
  };

  static $inject = [
    "$ngRedux",
    "PlanningSessionsActions",
    "TeamsActions",
    "$rootScope",
    "$scope",
    "$state",
    "PlanningSessionService",
    "CheckAccess",
    "$uibModal",
    "AnalyticsService",
    "PermissionsService",
    "$stateParams",
    "$q",
    "UIErrorHandlingService",
    "RoleService",
    "FeatureToggleV2Service",
    "ApprovalWorkflowSelectionModal",
    "OkrMethodologyFacade",
    "AccountService",
  ];

  constructor(
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    teamsActions: TeamsActions,
    private $rootScope: IRootScopeService,
    private $scope: ISessionFormScope,
    private $state: StateService,
    private planningSessionService: PlanningSessionService,
    private checkAccess: ICheckAccessFactory,
    private $uibModal: IModalService,
    private analyticsService: AnalyticsService,
    private permissionService: PermissionsService,
    private $stateParams: StateParams,
    private $q: IQService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private roleService: RoleService,
    private featureService: FeatureToggleV2Service,
    private approvalWorkflowSelectionModal: ApprovalWorkflowSelectionModal,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private accountService: AccountService
  ) {
    super();

    this.operationType = this.$stateParams.id ? "edit" : "create";

    this.onDestroy($ngRedux.connect(bindStateToCtrl)(this));
    $ngRedux.dispatch(planningSessionsActions.getSessionsIfMissing());
    $ngRedux.dispatch(teamsActions.fetchTeamsIfMissing());
  }

  $onInit(): void {
    this.analyticsService.track("Session Form Opened", { type: this.operationType });
    this.$scope.$on("sessionSelected", (evt, sessionId: string): void => {
      this.planningSession.parentId = sessionId;
    });
    this.indicators = {};
    this.triggerIndicator("loading", "start");
    this.initFormWhenSessionsAreFetched();
    this.methodologyPerSessionEnabled = this.accountService.getAccountSetting<boolean>("methodologyPerSessionEnabled");
  }

  get isSessionMethodologyVisible(): boolean {
    return !!(this.methodologyPerSessionEnabled || this.planningSession.settings);
  }

  inheritsPermissions(inherits: boolean): void {
    if (inherits) {
      if (this.operationType === "create") {
        this.planningSession.access = this.defaultAccess;
      } else {
        this.planningSession.access.inherits = true;
      }

      this.permissions.inheritsPermissions = true;
    } else {
      this.planningSession.access.inherits = false;
      this.permissions.inheritsPermissions = false;
    }
  }

  formatWeekday(weekday: string): string {
    const localEvery = localize("everyWeekday");
    const localOn = localize("onWeekday") !== "onWeekday" ? localize("onWeekday") + " " : "";

    return this.planningSession.cadenceType === "WEEKLY" ? `${localEvery} ${weekday}` : `${localOn}${weekday}`;
  }

  setCadenceStart(): void {
    if (this.planningSession.cadenceType === "MONTHLY") {
      this.planningSession.cadenceStart = {
        periodOfMonth: "firstDay",
      };
    } else {
      this.planningSession.cadenceStart = {
        weekday: "Monday",
      };
    }
  }

  setCadenceDay(): void {
    this.planningSession.cadenceStart.dayOfMonth = 1;
  }

  changeWorkflow = (reviewers: number): void => {
    if (!reviewers) {
      this.planningSession.workflow = null;
      this.sessionWorkflowSet = false;
      return;
    }

    if (reviewers && this.planningSession.workflow) {
      this.planningSession.workflow.reviewers = reviewers;

      return;
    }

    if (reviewers && !this.planningSession.workflow) {
      this.planningSession.workflow = {
        target: ["goal"],
        reviewers: reviewers,
      };
      this.sessionWorkflowSet = true;
    }
  };

  checkForConflicts(): void {
    this.triggerIndicator("saving", "start");
    this.setDateRange();
    this.clearSelectedFields();

    this.planningSessionService.checkForConflictsInSession(this.planningSession, "update").then(
      (conflicts) => {
        this.conflicts = conflicts;

        if (conflicts.totalCount === 0) {
          this.editSession();
        } else {
          this.triggerIndicator("saving", "stop");
          this.openConflictsModal(conflicts);
        }
      },
      (error) => this.triggerIndicator("saving", "error", error)
    );
  }

  save(): void {
    //In case user has edited selected fields but chose All fields option we dont want to send workflow editableKrFields and editableOkrFields to BE
    this.clearSelectedFields();

    if (this.operationType === "create") {
      this.createSession();
    } else {
      this.editSession();
    }
  }

  updateSettings(settings: ISessionSettings): void {
    this.sessionSettings = settings;
    this.setSessionSettings();
  }

  clearSelectedFields(): void {
    if (!this.isSelectedFieldsChecked && this.planningSession.workflow) {
      delete this.planningSession.workflow.editableOkrFields;
      delete this.planningSession.workflow.editableKrFields;
    }
  }

  disableSaveBtn(): boolean {
    return (
      angular.equals(this.planningSession, this.originalPlanningSession) &&
      dayjs(this.model.dateRange.startDate).diff(this.originalPlanningSession.start) === 0 &&
      dayjs(this.model.dateRange.endDate).diff(this.originalPlanningSession.end) === 0
    );
  }

  isSessionOngoing(): boolean {
    const startDateString = this.model.dateRange.startDate;
    return this.operationType === "edit" && dayjs().isAfter(dayjs(startDateString));
  }

  getAssignGoalPermissions(): AssigneeType {
    return this.okrMethodologyFacade.getAssignGoalPermissionsSetting(this.planningSession.settings);
  }

  shouldShowSelfApproveTooltip(): boolean {
    const assignGoalPermissions = this.getAssignGoalPermissions();
    return assignGoalPermissions === "team" || assignGoalPermissions === "both";
  }

  private setSessionSettings(): void {
    if (this.sessionSettings) {
      this.planningSession.settings = this.sessionSettings;
    } else {
      if (this.planningSession.settings) {
        delete this.planningSession.settings;
      }
    }
  }

  private triggerIndicator(indicatorType: "saving" | "loading", action: "start" | "stop" | "error", error?: IUIError): void {
    if (action === "start") {
      this.indicators[indicatorType] = { progress: true };
    } else if (action === "stop") {
      delete this.indicators[indicatorType];
    } else {
      this.indicators[indicatorType] = { error };
    }
  }

  private initFormWhenSessionsAreFetched(): IPromise<unknown> {
    this.originalPlanningSession = {};
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.sessionsFetched && this.teamsFetched,
        () => this.sessionsError || this.teamsError
      )
      .then(() => {
        this.operationType === "create" ? this.initOnCreate() : this.initOnEdit();
      })
      .catch((error) => {
        this.triggerIndicator("loading", "error", error);
      });
  }

  private initOnCreate(): void {
    this.planningSession = {
      title: "",
      access: {
        inherits: true,
        permissions: null,
      },
      color: this.colors[Math.floor(Math.random() * this.colors.length)],
      parentId: "",
      status: PlanningSessionStatus.OPEN,
      cadenceType: "WEEKLY" as CadenceType,
      cadenceStart: {
        weekday: "Monday",
      },
      workflow: null,
      cadenceTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isSelfApproveAllowed: true,
    };

    this.permissions = { inheritsPermissions: this.planningSession.access.inherits };

    this.$q
      .all([this.getDefaultAccessOnCreate(), this.isPermissionsSessionsAccountReadonlyFeatureAvailable()])
      .catch((error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: true }));
  }

  private getDefaultAccessOnCreate(): IPromise<void> {
    return this.permissionService.getDefaultAccess("session").then((access) => {
      this.defaultAccess = access;
      this.planningSession.access = this.defaultAccess;
    });
  }

  private isPermissionsSessionsAccountReadonlyFeatureAvailable(): IPromise<void> {
    return this.featureService.isFeatureAvailable("permissions-sessions-account-readonly").then((permissionsFeatureEnabled) => {
      if (!permissionsFeatureEnabled) {
        this.triggerIndicator("loading", "stop");
      } else {
        this.setPermissionsForReadonlyAccount();
      }
    });
  }

  private setPermissionsForReadonlyAccount(): void {
    this.roleService.getRoles().then((roles) => {
      this.planningSession.access = this.permissionService.getDefaultAccessForReadOnlyAccount(roles);
      this.permissions.inheritsPermissions = this.planningSession.access.inherits;
      this.triggerIndicator("loading", "stop");
    });
  }

  private initOnEdit(): void {
    const session = angular.copy(this.sessions[this.$stateParams.id]);
    session.cadenceType = session.cadence.type;

    this.permissions = {
      inheritsPermissions: session.access.inherits,
    };

    this.model.dateRange = {
      startDate: session.start,
      endDate: session.end,
    };

    if (!session.color) {
      session.color = this.colors[Math.floor(Math.random() * this.colors.length)];
    }

    if (session.settings) {
      this.sessionSettings = session.settings;
    }

    this.planningSession = session;

    // If we are editing a session we want to set workflow approval selected fields options checked if session ha selected fields
    this.isSelectedFieldsChecked = session.workflow?.editableKrFields?.length > 0 || session.workflow?.editableOkrFields?.length > 0;

    this.originalPlanningSession = angular.copy(session);

    this.processDynamicSubteamsPermissions();
  }

  private processDynamicSubteamsPermissions() {
    const promises: IPromise<void>[] = [];
    if (this.planningSession.access.permissions) {
      this.planningSession.access.permissions.forEach((p) => {
        if (p.principalKind === "team_and_subteams") {
          promises.push(this.setCorrectValuesToPermission(p));
        }
      });
    }

    this.$q.all(promises).then(
      () => {
        this.triggerIndicator("loading", "stop");
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "settingLanguage", inline: true })
    );
  }

  private setCorrectValuesToPermission(permission: IApiPermission): IPromise<void> {
    const deferred = this.$q.defer<void>();

    this.roleService
      .getRole(permission.principalId)
      .then((response: IDynamicSubteamsRole) => {
        permission.principalId = response.teamId;
        permission.principalKind = "team";
        permission.includeSubteams = true;
        deferred.resolve();
      })
      .catch((err) => {
        if (err.status === 404) {
          // Ignoring 404. It could receive 404 when team is deleted.
          deferred.resolve();
        } else {
          deferred.reject(err);
        }
      });

    return deferred.promise;
  }

  private setDateRange(): void {
    this.planningSession.start = this.model.dateRange.startDate;
    this.planningSession.end = this.model.dateRange.endDate;
  }

  private checkSessionAccess(): IPromise<void> {
    this.triggerIndicator("saving", "start");

    return this.checkAccess.checkAccess(this.planningSession.access, this.originalPlanningSession.access).then(
      (accessChecked: IAccessChecked) => {
        if (accessChecked.canSave === true) {
          this.planningSession.access = accessChecked.access;
          if (this.operationType === "edit" && !this.planningSession.access) {
            return this.permissionService.getDefaultAccessById("session", this.planningSession.id).then(
              (access) => {
                this.planningSession.access = access;
              },
              (error) => this.triggerIndicator("saving", "error", error)
            );
          }
        }
      },
      (error) => this.triggerIndicator("saving", "error", error)
    );
  }

  private createSession(): void {
    this.checkSessionAccess().then(() => {
      this.includeSubteamsPermissions().then(
        () => {
          this.setDateRange();
          this.planningSessionService.createPlanningSession(this.planningSession).then(
            (session) => {
              this.trackRecommendationHelpfulness(session);
              this.triggerIndicator("saving", "stop");
              this.$rootScope.$broadcast("sessionCreated", session);
              this.$ngRedux.dispatch(this.planningSessionsActions.addSession(session));
              // If the user created new session from cloning session modal, he will be redirected to the modal instead of session goals

              if (this.$stateParams.isCloningOperation) {
                this.$state.go("^.^");
              } else {
                this.$state.go("gtmhub.goals.all", { planningSessionId: session.id });
              }
            },
            (error) => this.triggerIndicator("saving", "error", error)
          );
        },
        (error) => this.triggerIndicator("saving", "error", error)
      );
    });
  }

  private trackRecommendationHelpfulness(session: IPlanningSession): void {
    //Tracking Session creation recommendations
    //recommendation is helpful if user doesn’t change start date
    this.analyticsService.track(recommendation, {
      is_useful: new Date(session.start).toDateString() === new Date(this.initialSessionDates.start).toDateString(),
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;start_date",
    });
    //recommendation is helpful if user doesn’t change end date
    this.analyticsService.track(recommendation, {
      is_useful: new Date(session.end).toDateString() === new Date(this.initialSessionDates.end).toDateString(),
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;end_date",
    });
    //recommendation is helpful if user doesn’t change cadence
    this.analyticsService.track(recommendation, {
      is_useful: session.cadence.type === "WEEKLY",
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;cadence",
    });
    //recommendation is helpful if user doesn’t change cadence day
    this.analyticsService.track(recommendation, {
      is_useful: session.cadenceStart.weekday === "Monday",
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;cadence_day",
    });
    //recommendation is helpful if session doesn’t have workflow
    this.analyticsService.track(recommendation, {
      is_useful: !session.workflow,
      flavor: Flavor.SMART_DEFAULT,
      action: "creates_session;approval_workflow",
    });
  }

  private includeSubteamsPermissions(): IPromise<void> {
    if (this.planningSession.access === null) {
      return this.$q.resolve();
    }

    // If teamsError exists - reject - can't include permissions.
    if (this.teamsError) {
      return this.$q.reject(this.teamsError);
    }

    // If teamsError not fetched - reject - can't include permissions.
    if (!this.teamsFetched) {
      return this.$q.reject(createUIError({ body: "teams not loaded" }));
    }

    const parentsWithIncludedSubteams = this.getTeamsPermissionsWithIncludedSubteams();
    if (!parentsWithIncludedSubteams.length) {
      return this.$q.resolve();
    } else {
      parentsWithIncludedSubteams.forEach((p) => (p.principalKind = "team_and_subteams"));
    }

    if (this.planningSession.access.permissions.length > 0) {
      this.planningSession.access.inherits = false;
    }

    return this.$q.resolve();
  }

  private getTeamsPermissionsWithIncludedSubteams = () => {
    return this.planningSession.access.permissions.filter((permission) => {
      return permission.principalKind === "team" && permission.includeSubteams;
    });
  };

  private getSubteamsPermissions = (teams: ICollection<ITeam>, parent: IApiPermission): IApiPermission[] => {
    const currentTeams = parent.includeSubteams ? [] : [parent];
    const children = teams.items
      .filter((t) => t.parentId === parent.principalId)
      .map((team) => {
        return {
          principalKind: "team" as IPrincipalKind,
          principalId: team.id,
          grant: parent.grant,
        };
      });
    for (const child of children) {
      currentTeams.push(...this.getSubteamsPermissions(teams, child));
    }

    return currentTeams;
  };

  openApprovalWorkflowSelectionSelection(): void {
    this.approvalWorkflowSelectionModal
      .open(this.planningSession.workflow.editableOkrFields, this.planningSession.workflow.editableKrFields, this.planningSession.id)
      .then(({ unSelectedOkrFields, unSelectedKrFields }) => {
        this.planningSession.workflow.editableOkrFields = unSelectedOkrFields;
        this.planningSession.workflow.editableKrFields = unSelectedKrFields;
      });
  }

  private editSession(): void {
    this.inheritsPermissions(this.permissions.inheritsPermissions);
    this.checkSessionAccess().then(() => {
      this.includeSubteamsPermissions().then(() => {
        this.planningSessionService.updatePlanningSession(this.planningSession).then(
          () => {
            this.triggerIndicator("saving", "stop");
            this.$state.go("^.^");
          },
          (error) => this.triggerIndicator("saving", "error", error)
        );
      });
    });
  }

  private openConflictsModal(conflicts: ICollection<IPlanningSessionConflict>): void {
    this.$uibModal.open({
      templateUrl: require("../../views/conflicts.jade"),
      windowClass: "medium",
      controller: "PlanningSessionConflictsCtrl",
      resolve: {
        conflicts: () => conflicts,
        session: () => this.planningSession,
        onSave: () => () => {
          this.save();
        },
        ignoreAndClone: () => false,
      },
    });
  }
}

export const SessionFormComponent: IComponentOptions = {
  templateUrl: require("./session-form.jade"),
  controller: SessionFormCtrl,
  bindings: {},
};
